import clsx from 'clsx';
import {
    markNotificationShownAction,
    saveFormResponsesThunk
} from 'actions/formsInvitation/FormsInvitationActions';
import { PetList } from 'components/ClientForm/PetList/PetList';
import {
    firstVisibleFormId,
    PendingForm,
    PendingFormList
} from 'components/ClientForm/PendingFormList/PendingFormList';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { Header } from 'components/Layout/Header/Header';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { Button } from 'components/UI/Button/Button';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getFormsInvitationAnswers,
    getFormsInvitationData,
    getNotifyAddPetShown,
    getNotifyAddQuestionShown
} from 'reducers/formsInvitation/FormsInvitationSelectors';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';

import classes from './PetFormPage.module.scss';
import { FormStatus, PetForm } from 'types/FormsInvitation';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { AddPet } from './AddPet/AddPet';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { AlertScreen, AlertVariant } from 'types/Alert';
import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { AlertSuccess } from '../AlertSuccess/AlertSuccess';
import { faSquareList } from '@fortawesome/pro-regular-svg-icons';

interface PetFormPageProps {
    currentStep: number;
    totalSteps: number;
    notifyAddQuestion: boolean;
    notifyAddPet: boolean;

    // Pets props
    pets: PetForm[];

    // Forms props (per pet)
    petForms: { [petUuid: string]: PendingForm[] };

    // Handlers
    onBack?: () => void;
    onSubmit: () => void;
}

export const PetFormPage: React.FC<PetFormPageProps> = props => {
    const dispatch = useDispatch();
    const marketplace = useSelector(getMarketplace);
    const invitationData = useSelector(getFormsInvitationData);
    const reduxAnswers = useSelector(getFormsInvitationAnswers);
    const notifyAddQuestionShown = useSelector(getNotifyAddQuestionShown);
    const notifyAddPetShown = useSelector(getNotifyAddPetShown);
    const isMobile = useMediaQuery('mobile-and-tablet');

    const [selectedPetUuid, setSelectedPetUuid] = React.useState<string | null>(
        props.pets.length > 0 ? (!isMobile ? props.pets[0].uuid : null) : null
    );
    const [expandedFormId, setExpandedFormId] = React.useState<number | null>(() => {
        const initialPetUuid = props.pets.length > 0 && !isMobile ? props.pets[0].uuid : null;
        return initialPetUuid ? firstVisibleFormId(props.petForms[initialPetUuid] || []) : null;
    });
    const [formAnswers, setFormAnswers] = React.useState<{
        [petUuid: string]: { [formId: number]: { [questionId: number]: string | string[] } };
    }>({});
    const [addNewPet, setAddNewPet] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!selectedPetUuid) {
            setExpandedFormId(null);
            return;
        }
        setExpandedFormId(firstVisibleFormId(props.petForms[selectedPetUuid] || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPetUuid]);

    // Initialize formAnswers from Redux (pre-filled answers from backend)
    React.useEffect(() => {
        if (reduxAnswers && Object.keys(formAnswers).length === 0) {
            // Organize answers by pet UUID
            const petAnswers: typeof formAnswers = {};

            props.pets.forEach(pet => {
                const petForms = props.petForms[pet.uuid] || [];
                petAnswers[pet.uuid] = {};

                petForms.forEach(form => {
                    const compositeKey = `${form.id}_${pet.id}`;
                    const reduxMap = reduxAnswers as Record<
                        string,
                        Record<number, string | string[]>
                    >;
                    const reduxVal = reduxMap[compositeKey];
                    if (reduxVal) {
                        petAnswers[pet.uuid][form.id] = reduxVal;
                    }
                });
            });

            setFormAnswers(petAnswers);
        }
    }, [reduxAnswers, props.pets, props.petForms, formAnswers]);

    React.useEffect(() => {
        if (props.currentStep == 1 && props.notifyAddQuestion && !notifyAddQuestionShown) {
            dispatch(
                addAlertThunk(
                    <AlertSuccess message="New questions have been added. Please review and answer them before submitting." />,
                    AlertScreen.FormInvitationPet,
                    AlertVariant.Success
                )
            );
            dispatch(markNotificationShownAction('addQuestion'));
        }
        if (props.notifyAddPet && !notifyAddPetShown) {
            dispatch(
                addAlertThunk(
                    <AlertSuccess
                        message="New pets were added. Please review and answer their questions."
                        icon={faSquareList}
                    />,
                    AlertScreen.FormInvitationPet,
                    AlertVariant.Success
                )
            );
            dispatch(markNotificationShownAction('addPet'));
        }
    }, [
        props.notifyAddPet,
        props.notifyAddQuestion,
        notifyAddQuestionShown,
        notifyAddPetShown,
        dispatch,
        props.currentStep
    ]);

    // Calculate progress percentage based on current step
    const progress = (props.currentStep / props.totalSteps) * 100;

    const handleToggle = (formId: number) => {
        setExpandedFormId(prev => (prev === formId ? null : formId));
    };

    const handleSave = (formId: number, answers: { [questionId: number]: string | string[] }) => {
        if (!selectedPetUuid) return;

        // Update local state immediately for UI
        setFormAnswers(prev => ({
            ...prev,
            [selectedPetUuid]: {
                ...(prev[selectedPetUuid] || {}),
                [formId]: answers
            }
        }));

        if (!invitationData) return;

        const petId = selectedPet?.id;
        if (!petId) return;

        dispatch(saveFormResponsesThunk(formId, answers, 'Pet', petId));
    };

    const isFormComplete = (petUuid: string, form: PendingForm): boolean => {
        const requiredQuestions = form.questions.filter(q => q.required);
        if (requiredQuestions.length === 0) return true;

        const petAnswers = formAnswers[petUuid] || {};
        const answers = petAnswers[form.id] || {};

        return requiredQuestions.every(q => {
            const answer = answers[q.id];
            if (Array.isArray(answer)) {
                return answer.length > 0;
            }
            return answer && answer.toString().trim().length > 0;
        });
    };

    // Calculate pet status dynamically based on form completion
    const getPetStatus = (petUuid: string): 'Pending' | 'Completed' => {
        const forms = props.petForms[petUuid] || [];
        if (forms.length === 0) return 'Completed';

        const allFormsCompleted = forms.every(form => isFormComplete(petUuid, form));
        return allFormsCompleted ? 'Completed' : 'Pending';
    };

    const handleBack = () => {
        if (isMobile && selectedPetUuid) {
            // En mobile, si hay una mascota seleccionada: limpiamos selección
            setSelectedPetUuid(null);
            setExpandedFormId(null);
        } else if (props.onBack) {
            // Comportamiento normal (desktop o sin pet seleccionado)
            props.onBack();
        }
    };

    // Update pets with dynamic status
    const petsWithUpdatedStatus: PetForm[] = props.pets.map(pet => ({
        ...pet,
        formsStatus: getPetStatus(pet.uuid)
    }));

    const selectedPet = petsWithUpdatedStatus.find(p => p.uuid === selectedPetUuid);
    const currentForms = selectedPetUuid ? props.petForms[selectedPetUuid] || [] : [];

    const formsWithStatus: PendingForm[] = currentForms.map(form => ({
        ...form,
        status:
            selectedPetUuid && isFormComplete(selectedPetUuid, form)
                ? FormStatus.Completed
                : FormStatus.Pending
    }));

    const petAnswers = selectedPetUuid ? formAnswers[selectedPetUuid] || {} : {};

    const allFormsCompleted = petsWithUpdatedStatus.every(pet => pet.formsStatus === 'Completed');

    const handleAddPetBack = () => {
        setAddNewPet(false);
        // Clear selection when coming back from add pet
        if (isMobile) {
            setSelectedPetUuid(null);
        }
    };

    return (
        <>
            {addNewPet ? (
                <AddPet
                    currentStep={props.currentStep}
                    totalSteps={props.totalSteps}
                    onBack={handleAddPetBack}
                />
            ) : (
                <TwoColumnsLayout
                    header={
                        <Header logo={marketplace.logo} progress={progress} onBack={handleBack} />
                    }
                >
                    <LeftColumn
                        className={clsx({
                            [classes.columnMobileVisible]: isMobile && !selectedPetUuid,
                            [classes.columnMobileHidden]: isMobile && selectedPetUuid
                        })}
                        footer={
                            isMobile && (
                                <Button
                                    fullWidth
                                    disabled={!allFormsCompleted}
                                    onClick={props.onSubmit}
                                >
                                    Submit
                                </Button>
                            )
                        }
                    >
                        <PetList
                            pets={petsWithUpdatedStatus}
                            selectedPetUuid={selectedPetUuid}
                            onSelectPet={setSelectedPetUuid}
                            onClickAddPet={() => setAddNewPet(true)}
                        />
                    </LeftColumn>

                    <RightColumn
                        className={clsx(classes.rightColumn, {
                            [classes.columnMobileVisible]: isMobile && !!selectedPetUuid,
                            [classes.columnMobileHidden]: isMobile && !selectedPetUuid
                        })}
                        contentClassName={classes.rightColumnContent}
                        footerContentClassName={classes.rightColumnFooterContent}
                        footer={
                            !isMobile && (
                                <Button
                                    fullWidth
                                    disabled={!allFormsCompleted}
                                    onClick={props.onSubmit}
                                >
                                    Submit
                                </Button>
                            )
                        }
                    >
                        <>
                            <MarketplaceInfo
                                hideLogo
                                marketplace={marketplace}
                                className={classes.marketplaceInfo}
                            />

                            <ErrorList
                                screen={AlertScreen.FormInvitationPet}
                                className="mt-5 mb-5"
                            />

                            {selectedPet && (
                                <PendingFormList
                                    title={`${selectedPet.name}'s Questions`}
                                    forms={formsWithStatus}
                                    expandedFormId={expandedFormId}
                                    answers={petAnswers}
                                    onToggle={handleToggle}
                                    onSave={handleSave}
                                />
                            )}
                        </>
                    </RightColumn>
                </TwoColumnsLayout>
            )}
        </>
    );
};
